<template>
    <Message
        v-bind="$attrs"
        :severity="severity"
        :closable="closable"
        :life="life"
        :class="messageClasses"
        @close="handleClose"
    >
        <template v-for="(_, slot) in $slots" #[slot]="scope" :key="slot">
            <slot
                v-bind="scope && scope.key !== undefined ? scope : {}"
                :name="slot"
            />
        </template>
    </Message>
</template>

<script>
import { getPrimeVueSeverity } from './utils/componentMappings.js';
/**
 * ENotification - Abstraction wrapper around PrimeVue's Message
 *
 * Usage:
 *   <e-notification>
 *     This is a notification
 *   </e-notification>
 *
 *   <e-notification type="is-success" has-icon>
 *     Your changes have been saved successfully!
 *   </e-notification>
 *
 *   <e-notification type="is-danger" closable @close="handleClose">
 *     An error occurred. Please try again.
 *   </e-notification>
 *
 *   <e-notification type="is-warning" :auto-close="true" :duration="5000">
 *     This notification will auto-close in 5 seconds
 *   </e-notification>
 *
 * Props (work in both modes):
 *   - type: String - Color type (is-primary, is-success, is-danger, etc.)
 *   - closable: Boolean - Show close button (default: true in PrimeVue, varies in Buefy)
 *   - has-icon: Boolean - Show icon (Buefy-specific, PrimeVue shows icons by default)
 *   - auto-close: Boolean - Enable auto-close (works with duration)
 *   - duration: Number - Auto-close duration in milliseconds (default: 5000)
 *
 * Events:
 *   - close: Emitted when notification is closed
 *
 * All Buefy b-notification props, events, and slots are supported in Buefy mode.
 */
export default {
    name: 'ENotification',
    inheritAttrs: false,
    props: {
        type: {
            type: String,
            default: undefined,
        },
        closable: {
            type: Boolean,
            default: true,
        },
        hasIcon: {
            type: Boolean,
            default: undefined,
        },
        autoClose: {
            type: Boolean,
            default: false,
        },
        duration: {
            type: Number,
            default: 5000,
        },
    },
    emits: ['close'],
    computed: {
        severity() {
            return this.type
                ? getPrimeVueSeverity(this.type.replace(/\bis-light\b|\blight\b/g, '').trim(), 'info')
                : 'info';
        },
        isLight() {
            // Check if the type contains "is-light" or just "light"
            return this.type && (this.type.includes('is-light') || this.type.includes(' light'));
        },
        life() {
            // PrimeVue uses 'life' prop for auto-close duration
            // If autoClose is true, use the duration, otherwise undefined (no auto-close)
            return this.autoClose ? this.duration : undefined;
        },
        messageClasses() {
            // Add -light suffix if this is a light variant
            const baseSeverity = this.severity;
            return this.isLight ? `e-notification-${baseSeverity}-light` : `e-notification-${baseSeverity}`;
        },
    },
    methods: {
        handleClose() {
            this.$emit('close');
        },
    },
};
</script>

<style lang="scss">
@import '~assets/scss/toolbox.scss';

/* Override PrimeVue Message colors with theme colors and white text */
.e-notification-success {
    --p-message-success-background: #{$success};
    --p-message-success-border-color: #{$success};
    --p-message-success-color: #{$white};
    --p-message-success-shadow: none;
    --p-message-text-font-weight: #{$weight-normal};
    --p-message-text-font-size: 1.125rem;
    --text-strong: var(--p-message-success-color);
    margin: 0;
    padding: 0.75rem 1rem;
}

.e-notification-info {
    --p-message-info-background: #{$info};
    --p-message-info-border-color: #{$info};
    --p-message-info-color: #{$white};
    --p-message-info-shadow: none;
    --p-message-text-font-weight: #{$weight-normal};
    --p-message-text-font-size: 1.125rem;
    --text-strong: var(--p-message-info-color);
    margin: 0;
    padding: 0.75rem 1rem;
}

/* Light variant styles */
.e-notification-success-light {
    --p-message-success-background: #{$success-light};
    --p-message-success-border-color: #{$success-light};
    --p-message-success-color: #{darken($success, 20%)};
    --p-message-success-shadow: none;
    --p-message-text-font-weight: #{$weight-normal};
    --p-message-text-font-size: 1.125rem;
    --text-strong: var(--p-message-success-color);
    margin: 0;
    padding: 0.75rem 1rem;
}

.e-notification-warn {
    --p-message-warn-background: #{$warning};
    --p-message-warn-border-color: #{$warning};
    --p-message-warn-color: #{rgba($black, 0.7)};
    --p-message-warn-shadow: none;
    --p-message-text-font-weight: #{$weight-normal};
    --p-message-text-font-size: 1.125rem;
    --text-strong: var(--p-message-warn-color);
    margin: 0;
    padding: 0.75rem 1rem;
}

.e-notification-error {
    --p-message-error-background: #{$danger};
    --p-message-error-border-color: #{$danger};
    --p-message-error-color: #{$white};
    --p-message-error-shadow: none;
    --p-message-text-font-weight: #{$weight-normal};
    --p-message-text-font-size: 1.125rem;
    --text-strong: var(--p-message-error-color);
    margin: 0;
    padding: 0.75rem 1rem;
}

.e-notification-secondary {
    --p-message-secondary-background: #{$grey};
    --p-message-secondary-border-color: #{$grey};
    --p-message-secondary-color: #{$white};
    --p-message-secondary-shadow: none;
    --p-message-text-font-weight: #{$weight-normal};
    --p-message-text-font-size: 1.125rem;
    --text-strong: var(--p-message-secondary-color);
    margin: 0;
    padding: 0.75rem 1rem;
}

.e-notification-contrast {
    --p-message-contrast-background: #{$grey-darker};
    --p-message-contrast-border-color: #{$grey-darker};
    --p-message-contrast-color: #{$white};
    --p-message-contrast-shadow: none;
    --p-message-text-font-weight: #{$weight-normal};
    --p-message-text-font-size: 1.125rem;
    --text-strong: var(--p-message-contrast-color);
    margin: 0;
    padding: 0.75rem 1rem;
}

.e-notification-info-light {
    --p-message-info-background: #{$info-light};
    --p-message-info-border-color: #{$info-light};
    --p-message-info-color: #{$info};
    --p-message-info-shadow: none;
    --p-message-text-font-weight: #{$weight-normal};
    --p-message-text-font-size: 1.125rem;
    --text-strong: var(--p-message-info-color);
    margin: 0;
    padding: 0.75rem 1rem;
}

.e-notification-warn-light {
    --p-message-warn-background: #{$warning-light};
    --p-message-warn-border-color: #{$warning-light};
    --p-message-warn-color: #{darken($warning, 35%)};
    --p-message-warn-shadow: none;
    --p-message-text-font-weight: #{$weight-normal};
    --p-message-text-font-size: 1.125rem;
    --text-strong: var(--p-message-warn-color);
    margin: 0;
    padding: 0.75rem 1rem;
}

.e-notification-error-light {
    --p-message-error-background: #{$danger-light};
    --p-message-error-border-color: #{$danger-light};
    --p-message-error-shadow: none;
    --p-message-text-font-weight: #{$weight-normal};
    --p-message-text-font-size: 1.125rem;
    --p-message-error-color: #{darken($danger, 20%)};
    --text-strong: var(--p-message-error-color);
    margin: 0;
    padding: 0.75rem 1rem;
}

.e-notification-secondary-light {
    --p-message-secondary-background: #{$grey-light};
    --p-message-secondary-border-color: #{$grey-light};
    --p-message-secondary-color: #{darken($grey, 30%)};
    --p-message-secondary-shadow: none;
    --p-message-text-font-weight: #{$weight-normal};
    --p-message-text-font-size: 1.125rem;
    --text-strong: var(--p-message-secondary-color);
    margin: 0;
    padding: 0.75rem 1rem;
}

.e-notification-contrast-light {
    --p-message-contrast-background: #{$grey-lighter};
    --p-message-contrast-border-color: #{$grey-lighter};
    --p-message-contrast-color: #{$grey-darker};
    --p-message-contrast-shadow: none;
    --p-message-text-font-weight: #{$weight-normal};
    --p-message-text-font-size: 1.125rem;
    --text-strong: var(--p-message-contrast-color);
    margin: 0;
    padding: 0.75rem 1rem;
}

/* Ensure icon is white for dark variants */
.e-notification-success .p-message-icon,
.e-notification-info .p-message-icon,
.e-notification-warn .p-message-icon,
.e-notification-error .p-message-icon,
.e-notification-secondary .p-message-icon,
.e-notification-contrast .p-message-icon {
    color: white;
    padding: 1rem;
}

/* Icon color for light variants matches text color */
.e-notification-success-light .p-message-icon,
.e-notification-info-light .p-message-icon,
.e-notification-warn-light .p-message-icon,
.e-notification-error-light .p-message-icon,
.e-notification-secondary-light .p-message-icon,
.e-notification-contrast-light .p-message-icon {
    padding: 1rem;
}

/* Style close button with circular background and position in top right */
.e-notification-success .p-message-close-button,
.e-notification-success-light .p-message-close-button,
.e-notification-info .p-message-close-button,
.e-notification-info-light .p-message-close-button,
.e-notification-warn .p-message-close-button,
.e-notification-warn-light .p-message-close-button,
.e-notification-error .p-message-close-button,
.e-notification-error-light .p-message-close-button,
.e-notification-secondary .p-message-close-button,
.e-notification-secondary-light .p-message-close-button,
.e-notification-contrast .p-message-close-button,
.e-notification-contrast-light .p-message-close-button {
    position: absolute;
    inset: 0.5rem 0.5rem auto auto;
    width: 1.25rem;
    height: 1.25rem;
    min-width: 1.25rem;
    min-height: 1.25rem;
    max-width: 1.25rem;
    max-height: 1.25rem;
    border-radius: 50%;
    background-color: rgba(0, 0, 0, 0.2);
    color: $white;
    transition: background-color 0.2s ease;
    border: none;
    outline: none;
}

/* Make the X icon smaller and bolder inside the button */
.e-notification-success .p-message-close-button svg,
.e-notification-success-light .p-message-close-button svg,
.e-notification-info .p-message-close-button svg,
.e-notification-info-light .p-message-close-button svg,
.e-notification-warn .p-message-close-button svg,
.e-notification-warn-light .p-message-close-button svg,
.e-notification-error .p-message-close-button svg,
.e-notification-error-light .p-message-close-button svg,
.e-notification-secondary .p-message-close-button svg,
.e-notification-secondary-light .p-message-close-button svg,
.e-notification-contrast .p-message-close-button svg,
.e-notification-contrast-light .p-message-close-button svg {
    width: 0.625rem;
    height: 0.625rem;
    display: block;
    margin: auto;
    filter: drop-shadow(0 0 0.5px currentColor) drop-shadow(0 0 0.5px currentColor);
}

/* Ensure the icon within the SVG is also centered */
.e-notification-success .p-message-close-button svg > *,
.e-notification-success-light .p-message-close-button svg > *,
.e-notification-info .p-message-close-button svg > *,
.e-notification-info-light .p-message-close-button svg > *,
.e-notification-warn .p-message-close-button svg > *,
.e-notification-warn-light .p-message-close-button svg > *,
.e-notification-error .p-message-close-button svg > *,
.e-notification-error-light .p-message-close-button svg > *,
.e-notification-secondary .p-message-close-button svg > *,
.e-notification-secondary-light .p-message-close-button svg > *,
.e-notification-contrast .p-message-close-button svg > *,
.e-notification-contrast-light .p-message-close-button svg > * {
    display: block;
    margin: auto;
}

.e-notification-success .p-message-close-button:hover,
.e-notification-success-light .p-message-close-button:hover,
.e-notification-info .p-message-close-button:hover,
.e-notification-info-light .p-message-close-button:hover,
.e-notification-warn .p-message-close-button:hover,
.e-notification-warn-light .p-message-close-button:hover,
.e-notification-error .p-message-close-button:hover,
.e-notification-error-light .p-message-close-button:hover,
.e-notification-secondary .p-message-close-button:hover,
.e-notification-secondary-light .p-message-close-button:hover,
.e-notification-contrast .p-message-close-button:hover,
.e-notification-contrast-light .p-message-close-button:hover {
    background-color: rgba(0, 0, 0, 0.5);
}

/* Make the notification container relative for absolute positioning */
.e-notification-success,
.e-notification-success-light,
.e-notification-info,
.e-notification-info-light,
.e-notification-warn,
.e-notification-warn-light,
.e-notification-error,
.e-notification-error-light,
.e-notification-secondary,
.e-notification-secondary-light,
.e-notification-contrast,
.e-notification-contrast-light {
    position: relative;
}

/*
 * Light notification variants use compile-time pastel Bulma colors; on dark UI they can
 * wash out or clash. Use overlay/surface tokens (same family as popover/announcement surfaces).
 */
html.app-dark,
html.dark-mode {
    .e-notification-info-light {
        --p-message-info-background: var(--surface-overlay);
        --p-message-info-border-color: var(--surface-border);
        --p-message-info-color: var(--p-text-color);
        --p-message-text-font-weight: #{$weight-normal};
        --text-strong: var(--p-primary-color);
    }

    .e-notification-info-light .p-message-icon {
        color: var(--p-primary-color);
    }

    .e-notification-success-light {
        --p-message-success-background: var(--surface-overlay);
        --p-message-success-border-color: var(--surface-border);
        --p-message-success-color: var(--p-text-color);
        --text-strong: #{$success};
    }

    .e-notification-success-light .p-message-icon {
        color: #{$success};
    }

    .e-notification-warn-light {
        --p-message-warn-background: var(--surface-overlay);
        --p-message-warn-border-color: var(--surface-border);
        --p-message-warn-color: var(--p-text-color);
        --text-strong: #{darken($warning, 12%)};
    }

    .e-notification-warn-light .p-message-icon {
        color: #{darken($warning, 12%)};
    }

    .e-notification-error-light {
        --p-message-error-background: var(--surface-overlay);
        --p-message-error-border-color: var(--surface-border);
        --p-message-error-color: var(--p-text-color);
        --text-strong: #{$danger};
    }

    .e-notification-error-light .p-message-icon {
        color: #{$danger};
    }

    .e-notification-secondary-light {
        --p-message-secondary-background: var(--surface-overlay);
        --p-message-secondary-border-color: var(--surface-border);
        --p-message-secondary-color: var(--p-text-color);
        --text-strong: var(--p-text-color);
    }

    .e-notification-secondary-light .p-message-icon {
        color: var(--p-text-muted-color);
    }

    .e-notification-contrast-light {
        --p-message-contrast-background: var(--surface-overlay);
        --p-message-contrast-border-color: var(--surface-border);
        --p-message-contrast-color: var(--p-text-color);
        --text-strong: var(--p-text-color);
    }

    .e-notification-contrast-light .p-message-icon {
        color: var(--p-text-muted-color);
    }

    .e-notification-info-light .p-message-close-button,
    .e-notification-success-light .p-message-close-button,
    .e-notification-warn-light .p-message-close-button,
    .e-notification-error-light .p-message-close-button,
    .e-notification-secondary-light .p-message-close-button,
    .e-notification-contrast-light .p-message-close-button {
        background-color: color-mix(in srgb, var(--p-text-color) 12%, transparent);
        color: var(--p-text-color);
    }

    .e-notification-info-light .p-message-close-button:hover,
    .e-notification-success-light .p-message-close-button:hover,
    .e-notification-warn-light .p-message-close-button:hover,
    .e-notification-error-light .p-message-close-button:hover,
    .e-notification-secondary-light .p-message-close-button:hover,
    .e-notification-contrast-light .p-message-close-button:hover {
        background-color: color-mix(in srgb, var(--p-text-color) 22%, transparent);
    }
}
</style>

